import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { addUserToCommunity } from '@/api/accounts';
import { getCommunities } from '@/api/communities';

const schema = zod.object({
  community_id: zod.string().min(1, { message: 'Community is required' }),
  role_name: zod.string().min(1, { message: 'Role is required' }),
});

type Values = zod.infer<typeof schema>;

interface AddCommunityModalProps {
  accountId: string;
  userId: string;
  existingCommunityIds: string[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const communityRoles = [
  { value: 'community_manager', label: 'Community Manager' },
  { value: 'community_member', label: 'Community Member' },
];

export function AddCommunityModal({
  accountId,
  userId,
  existingCommunityIds,
  open,
  onClose,
  onSuccess,
}: AddCommunityModalProps): React.JSX.Element {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      community_id: '',
      role_name: '',
    },
  });

  const { data: communitiesData } = useQuery({
    queryKey: ['communities'],
    queryFn: getCommunities,
  });

  // Filter out communities that the user is already assigned to
  const availableCommunities = React.useMemo(() => {
    if (!communitiesData?.communities) return [];
    return communitiesData.communities.filter(
      (community) => !existingCommunityIds.includes(community.id)
    );
  }, [communitiesData, existingCommunityIds]);

  const mutation = useMutation({
    mutationFn: (data: Values) => addUserToCommunity(accountId, userId, data),
    onSuccess: () => {
      toast.success('User added to community successfully');
      reset();
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add user to community');
    },
  });

  const onSubmit = React.useCallback(
    async (values: Values): Promise<void> => {
      mutation.mutate(values);
    },
    [mutation]
  );

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>Add User to Community</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Controller
            control={control}
            name="community_id"
            render={({ field }) => (
              <FormControl error={Boolean(errors.community_id)} fullWidth>
                <InputLabel>Community</InputLabel>
                <Select {...field} label="Community">
                  {availableCommunities.map((community) => (
                    <MenuItem key={community.id} value={community.id}>
                      {community.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.community_id && (
                  <FormHelperText>{errors.community_id.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="role_name"
            render={({ field }) => (
              <FormControl error={Boolean(errors.role_name)} fullWidth>
                <InputLabel>Role</InputLabel>
                <Select {...field} label="Role">
                  {communityRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role_name && (
                  <FormHelperText>{errors.role_name.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={mutation.isPending} type="submit" variant="contained">
          {mutation.isPending ? 'Adding...' : 'Add to Community'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
